import { cn, getCardVariant } from '../../lib/utils';

/**
 * AnimatedCard - A card component with variant support and optional animations
 *
 * @param {string} variant - 'default' | 'hero' | 'secondary' | 'muted' | 'interactive' | 'success' | 'warning' | 'destructive' | 'glow' | 'glow-filled' | 'glow-success' | 'glow-warning' | 'glow-destructive' | 'glow-info' | 'glow-interactive' | 'glow-hero'
 * @param {string} status - Optional status for status-driven styling
 * @param {boolean} glow - Use glowing border style (lightweight alternative to bg variants)
 * @param {boolean} noPadding - Remove default padding
 * @param {boolean} noAnimation - Disable entry animation
 * @param {number} staggerIndex - Index for staggered animations
 */
export function AnimatedCard({
  variant = 'default',
  status,
  glow = false,
  noPadding = false,
  noAnimation = false,
  staggerIndex = 0,
  className,
  children,
  ...props
}) {
  // Status-driven border colors (solid bg style)
  const statusBorders = {
    pending: 'border-muted',
    checking: 'border-primary animate-pulse-border',
    live: 'border-success/30',
    removed: 'border-destructive/30',
    '404': 'border-destructive/30',
    '5xx': 'border-destructive/30',
    timeout: 'border-warning/30',
    nofollow_changed: 'border-warning/30',
    redirected: 'border-warning/30',
  };

  // Status-driven backgrounds (solid bg style)
  const statusBgs = {
    checking: 'bg-primary/5',
    live: 'bg-success/5',
    removed: 'bg-destructive/5',
    '404': 'bg-destructive/5',
    '5xx': 'bg-destructive/5',
    timeout: 'bg-warning/5',
  };

  // Status-driven glowing border classes
  const statusGlow = {
    pending: '',
    checking: 'glow-pulse',
    live: 'glow-success',
    removed: 'glow-destructive',
    '404': 'glow-destructive',
    '5xx': 'glow-destructive',
    timeout: 'glow-warning',
    nofollow_changed: 'glow-warning',
    redirected: 'glow-warning',
  };

  const animationStyle = !noAnimation
    ? {
        animationDelay: `${staggerIndex * 50}ms`,
        animationFillMode: 'backwards',
      }
    : {};

  // Determine if using glow variant
  const isGlowVariant = glow || variant.startsWith('glow');

  return (
    <div
      className={cn(
        'rounded-xl transition-all duration-300',
        // Apply variant (glow variants don't need border class since it's in CSS)
        isGlowVariant ? getCardVariant(glow ? 'glow-filled' : variant) : cn('border', getCardVariant(variant)),
        // Apply status styling
        status && (isGlowVariant ? statusGlow[status] : cn(statusBorders[status], statusBgs[status])),
        !noPadding && 'p-4',
        !noAnimation && 'animate-fade-in',
        className
      )}
      style={animationStyle}
      {...props}
    >
      {children}
    </div>
  );
}

/**
 * CardHeader - Header section for cards
 */
export function CardHeader({ className, children, ...props }) {
  return (
    <div className={cn('flex flex-col space-y-1.5 pb-4', className)} {...props}>
      {children}
    </div>
  );
}

/**
 * CardTitle - Title for card header
 */
export function CardTitle({ className, children, ...props }) {
  return (
    <h3 className={cn('font-semibold leading-none tracking-tight text-foreground', className)} {...props}>
      {children}
    </h3>
  );
}

/**
 * CardDescription - Description text for card header
 */
export function CardDescription({ className, children, ...props }) {
  return (
    <p className={cn('text-sm text-muted-foreground', className)} {...props}>
      {children}
    </p>
  );
}

/**
 * CardContent - Main content area
 */
export function CardContent({ className, children, ...props }) {
  return (
    <div className={cn('', className)} {...props}>
      {children}
    </div>
  );
}

/**
 * CardFooter - Footer section
 */
export function CardFooter({ className, children, ...props }) {
  return (
    <div className={cn('flex items-center pt-4', className)} {...props}>
      {children}
    </div>
  );
}

export default AnimatedCard;
